import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DocTop } from '@/components/shell/DocTop';
import { PageCanvas } from '@/components/shell/PageCanvas';
import { Section } from '@/components/ui/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMe } from '@/lib/useMe';
import { useWallet } from '@/lib/walletHooks';
import { tierLabel } from '@/lib/tiers';
import { authClient, useSession, signOut } from '@/lib/authClient';
import { notifyError } from '@/lib/useMutationToast';
import { fieldAria } from '@/lib/formA11y';
import {
  getThemePreference,
  setThemePreference,
  resolveIsDark,
  applyIsDark,
  type ThemePreference,
} from './lib/theme';

const ROLE_LABELS: Record<'owner' | 'admin' | 'member', string> = {
  owner: 'Proprietar',
  admin: 'Administrator',
  member: 'Utilizator',
};

const fieldLabelClass = 'text-[11.5px] font-medium uppercase tracking-[0.06em] text-ink-3';

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Introduceți parola curentă'),
    newPassword: z.string().min(8, 'Parola trebuie să aibă cel puțin 8 caractere'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Parolele nu coincid',
    path: ['confirmPassword'],
  });
type ChangePasswordFields = z.infer<typeof changePasswordSchema>;

const CHANGE_PASSWORD_FALLBACK = 'Schimbarea parolei a eșuat. Verificați parola curentă.';

/** better-auth's built-in validation errors (`BASE_ERROR_CODES` — e.g.
 *  "Invalid password") are fixed English strings, not the server's own
 *  `{ error }` Romanian contract (CLAUDE.md's "Error responses are `{error}`
 *  in Romanian" applies to our own routes, not better-auth's). Surface the
 *  message only when it actually looks like Romanian copy (diacritics);
 *  otherwise fall back to the fixed RO message — in practice this always
 *  takes the fallback branch today, kept defensive for future localization. */
function changePasswordErrorMessage(message: string | undefined): string {
  if (message && /[ăâîșț]/i.test(message)) return message;
  return CHANGE_PASSWORD_FALLBACK;
}

function ProfilSection() {
  const { data: me } = useMe();
  const { data: session } = useSession();
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFields>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  async function onSubmit(values: ChangePasswordFields) {
    setFormError(null);
    setSuccess(false);
    const { error } = await authClient.changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      revokeOtherSessions: false,
    });
    if (error) {
      setFormError(changePasswordErrorMessage(error.message));
      return;
    }
    setSuccess(true);
    reset();
  }

  return (
    <>
      <Section title="Profil">
        <dl className="flex flex-wrap gap-x-10 gap-y-3 text-[13.5px]">
          <div>
            <dt className={fieldLabelClass}>Nume</dt>
            <dd className="mt-0.5 text-[13.5px] text-ink">{session?.user.name ?? '—'}</dd>
          </div>
          <div>
            <dt className={fieldLabelClass}>Email</dt>
            <dd className="mt-0.5 text-[13.5px] text-ink">{me?.email ?? '—'}</dd>
          </div>
        </dl>
      </Section>

      <Section title="Parolă">
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="current-password" className="text-[13.5px] font-medium text-ink-2">
                Parolă curentă
              </Label>
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                className="h-9"
                {...register('currentPassword')}
                {...fieldAria(!!errors.currentPassword, 'current-password-error')}
              />
              {errors.currentPassword && (
                <p id="current-password-error" className="text-[12.5px] text-bad">{errors.currentPassword.message}</p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-password" className="text-[13.5px] font-medium text-ink-2">
                Parolă nouă
              </Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                className="h-9"
                {...register('newPassword')}
                {...fieldAria(!!errors.newPassword, 'new-password-error')}
              />
              {errors.newPassword && <p id="new-password-error" className="text-[12.5px] text-bad">{errors.newPassword.message}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="confirm-password" className="text-[13.5px] font-medium text-ink-2">
                Confirmă parola nouă
              </Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                className="h-9"
                {...register('confirmPassword')}
                {...fieldAria(!!errors.confirmPassword, 'confirm-password-error')}
              />
              {errors.confirmPassword && (
                <p id="confirm-password-error" className="text-[12.5px] text-bad">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" size="sm" disabled={isSubmitting}>
              {isSubmitting ? 'Se salvează…' : 'Schimbă parola'}
            </Button>
            {success && <p className="text-[12.5px] text-ok">Parolă schimbată.</p>}
            {formError && <p className="text-[12.5px] text-bad">{formError}</p>}
          </div>
        </form>
      </Section>
    </>
  );
}

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Luminoasă' },
  { value: 'dark', label: 'Întunecată' },
  { value: 'system', label: 'Sistem' },
];

function TemaSection() {
  const [pref, setPref] = useState<ThemePreference>(() => getThemePreference());

  // Applies the resolved theme whenever the preference changes, and — only
  // while in 'system' mode — follows live OS changes via a matchMedia
  // listener that's torn down on unmount AND whenever `pref` moves away from
  // 'system' (the effect cleanup runs before every re-run, not just unmount).
  useEffect(() => {
    setThemePreference(pref);
    if (pref !== 'system') {
      applyIsDark(resolveIsDark(pref, false));
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    applyIsDark(resolveIsDark('system', mq.matches));
    const onChange = (e: MediaQueryListEvent) => applyIsDark(resolveIsDark('system', e.matches));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  return (
    <Section title="Temă">
      <Label className="text-[13.5px] font-medium text-ink-2">Temă</Label>
      <div
        role="group"
        aria-label="Temă"
        className="mt-2 inline-flex gap-1 rounded-md border border-rule p-0.5 text-[13.5px] font-medium"
      >
        {THEME_OPTIONS.map((opt) => {
          const active = pref === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => setPref(opt.value)}
              className={active ? 'rounded px-3 py-1.5 bg-wash text-ink' : 'rounded px-3 py-1.5 text-ink-3 hover:text-ink-2'}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[12.5px] text-ink-3">
        „Sistem" urmărește preferința sistemului de operare cât timp această pagină este deschisă.
      </p>
    </Section>
  );
}

function OrganizatieSection() {
  const { data: me } = useMe();
  const { data: wallet } = useWallet();
  const isAdmin = me?.orgRole === 'owner' || me?.orgRole === 'admin';

  return (
    <Section title="Organizație">
      <dl className="flex flex-wrap gap-x-10 gap-y-3 text-[13.5px]">
        <div>
          <dt className={fieldLabelClass}>Nume</dt>
          <dd className="mt-0.5 text-[13.5px] text-ink">{me?.orgName ?? '—'}</dd>
        </div>
        <div>
          <dt className={fieldLabelClass}>Rol</dt>
          <dd className="mt-0.5 text-[13.5px] text-ink">{me ? ROLE_LABELS[me.orgRole] : '—'}</dd>
        </div>
        {wallet && (
          <>
            <div>
              <dt className={fieldLabelClass}>Plan</dt>
              <dd className="mt-0.5 text-[13.5px] text-ink">{tierLabel(wallet.tierId)}</dd>
            </div>
            <div>
              <dt className={fieldLabelClass}>Realimentare automată</dt>
              <dd className="mt-0.5 text-[13.5px] text-ink">{wallet.autoRefill ? 'Activă' : 'Inactivă'}</dd>
            </div>
          </>
        )}
      </dl>
      {isAdmin && <p className="mt-3 text-[12.5px] text-ink-3">Plafon de depășire — în curând.</p>}
    </Section>
  );
}

function DeconectareSection() {
  return (
    <Section title="Cont">
      <Button
        type="button"
        variant="destructive"
        size="sm"
        onClick={() => {
          void signOut()
            .then(() => { window.location.href = '/login'; })
            .catch((e) => notifyError(e, 'Deconectarea a eșuat. Reîncercați.'));
        }}
      >
        Deconectare
      </Button>
    </Section>
  );
}

export function SetariPage() {
  return (
    <>
      <DocTop />
      <PageCanvas width="form">
        <div className="mt-8">
          <h1 className="text-[32px] font-[650] text-ink">Setări</h1>
          <div className="mt-1 border-b-2 border-ink pb-3" />

          <div className="mt-8 space-y-6">
            <ProfilSection />
            <TemaSection />
            <OrganizatieSection />
            <DeconectareSection />
          </div>
        </div>
      </PageCanvas>
    </>
  );
}
